import { Modal } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@xver/components/Atoms';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { ME_QUERY_KEY } from '@xver/hooks/useMeQuery';
import { requestAuthUser } from '@xver/store/auth/auth.actions';

import { useChargeStatusQuery } from '../hooks/useChargeStatusQuery';

const COUNTDOWN_SECONDS = 15 * 60;

function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

interface PromptPayQRModalProps {
    opened: boolean;
    qrCode: string;
    sourceID: string;
    onSuccess: (amount: number) => void;
    onClose: () => void;
}

export default function PromptPayQRModal({
    opened,
    qrCode,
    sourceID,
    onSuccess,
    onClose,
}: PromptPayQRModalProps) {
    const { t } = useTranslation('topup');
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const [timeRemaining, setTimeRemaining] = useState(COUNTDOWN_SECONDS);

    const { data: charge } = useChargeStatusQuery(sourceID, opened);

    useEffect(() => {
        if (!opened) {
            setTimeRemaining(COUNTDOWN_SECONDS);
            return;
        }
        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onClose();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [opened, onClose]);

    useEffect(() => {
        if (!opened) return;
        if (charge?.status === 'successful') {
            dispatch(requestAuthUser());
            queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            onSuccess(charge.amount);
        }
    }, [charge?.status, charge?.amount, opened, dispatch, queryClient, onSuccess]);

    const expiring = timeRemaining < 60;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('modal.qr.title')}
            centered
            size="sm"
            overlayProps={{ backgroundOpacity: 0.7, blur: 4 }}
        >
            <div className="flex flex-col items-center gap-5 pb-2">
                <p className="text-sm text-fg/70 text-center">{t('modal.qr.subtitle')}</p>

                <div
                    className="inline-flex items-center justify-center gap-2 px-2 py-1"
                    style={{
                        borderColor: expiring ? 'rgba(249,115,22,0.45)' : 'var(--line)',
                        color: expiring
                            ? 'var(--color-status-warning)'
                            : 'color-mix(in srgb, var(--fg) 85%, transparent)',
                    }}
                >
                    <span className="text-xl font-bold font-mono tabular-nums">
                        {formatTime(timeRemaining)}
                    </span>
                </div>

                {qrCode && (
                    <div
                        className="p-3 rounded-md bg-white"
                        style={{
                            boxShadow: '0 0 0 2px color-mix(in srgb, var(--fg) 8%, transparent)',
                        }}
                    >
                        <img
                            src={qrCode}
                            alt="PromptPay QR Code"
                            className="w-full max-w-60 h-auto"
                        />
                    </div>
                )}

                <Button variant="secondary" onClick={onClose} width="100%">
                    {t('modal.qr.cancel')}
                </Button>
            </div>
        </Modal>
    );
}
